// src/infrastructure/paymentComplement/HttpPaymentComplementRepository.ts
import type { HttpClient } from '../http/HttpClient'
import type {
  ContactDraft,
  OverdueComplement,
  PaymentComplementRepository,
  PendingProvidersPage,
  PendingProvidersQuery,
  ProviderContact,
  ProviderWithOverdueComplements,
} from '@/domain/paymentComplement/PaymentComplement'

interface RawObligation {
  payment_id: number
  payment_number: number
  payment_date: string
  due_date: string
  amount: number
  currency: string
  invoice_id: number
  invoice_number: string
}

interface RawContact {
  channel: ProviderContact['channel']
  note?: string | null
  contacted_at: string
  contacted_by: string
}

interface RawPendingProvider {
  provider_id: number
  provider_name: string
  provider_national_id: string
  overdue_count: number
  overdue_amounts?: { currency: string, amount: number }[]
  oldest_due_date: string
  payments?: RawObligation[]
  last_contact?: RawContact | null
  is_exempt?: boolean
}

interface RawPendingPage {
  count: number
  results?: RawPendingProvider[]
}

function toObligation(raw: RawObligation): OverdueComplement {
  return {
    paymentId: raw.payment_id,
    paymentNumber: raw.payment_number,
    paymentDate: raw.payment_date,
    dueDate: raw.due_date,
    amount: raw.amount,
    currency: raw.currency,
    invoiceId: raw.invoice_id,
    invoiceNumber: raw.invoice_number,
  }
}

function toProvider(raw: RawPendingProvider): ProviderWithOverdueComplements {
  return {
    providerId: raw.provider_id,
    name: raw.provider_name,
    nationalId: raw.provider_national_id,
    overdueCount: raw.overdue_count,
    overdueAmounts: raw.overdue_amounts ?? [],
    oldestDueDate: raw.oldest_due_date,
    payments: (raw.payments ?? []).map(toObligation),
    isExempt: raw.is_exempt ?? false,
    lastContact: raw.last_contact
      ? {
          channel: raw.last_contact.channel,
          note: raw.last_contact.note ?? null,
          contactedAt: raw.last_contact.contacted_at,
          contactedBy: raw.last_contact.contacted_by,
        }
      : null,
  }
}

export class HttpPaymentComplementRepository implements PaymentComplementRepository {
  constructor(private http: HttpClient) {}

  async fetchPendingProviders(query: PendingProvidersQuery): Promise<PendingProvidersPage> {
    const params: Record<string, string> = {
      page: String(query.page),
      page_size: String(query.pageSize),
      sort_by: query.sortBy,
      sort_order: query.sortOrder,
    }
    // Sent only when there is something to search: the backend matches with `icontains`, and an
    // empty value would still be a filter the endpoint has to parse.
    if (query.search) params.search = query.search

    const raw = await this.http
      .url('purchases/payment-complements/pending')
      .params(params)
      .get<RawPendingPage>()

    return {
      providers: (raw.results ?? []).map(toProvider),
      count: raw.count ?? 0,
    }
  }

  async logContact(providerId: number, draft: ContactDraft): Promise<void> {
    await this.http
      .url(`purchases/payment-complements/providers/${providerId}/contact`)
      .body(draft.note ? { channel: draft.channel, note: draft.note } : { channel: draft.channel })
      .post<void>()
  }
}
